import {
  applyAnnotationsToAttachments,
  DeleteFileS3Result,
  deleteServiceRequestFileFromS3,
  prepareServiceRequestCreationPayload,
  separateAttachments,
  UploadResult,
  uploadServiceRequestFileToS3,
} from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/helpers';
import { toast } from 'react-toastify';
import {  createServiceRequest } from 'src/services/client';
import { useDispatch } from 'react-redux';
import { fetchSuiteCounts } from 'src/store/actions/suites';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { store } from 'src/store';
import { fetchPropertyCounts } from 'src/store/actions/properties';
import { useState } from 'react';
import {
  ServiceRequestCreationNote,
  ServiceRequestCreationPayload,
} from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/serviceRequestFormtypes';
import { ServiceRequestFormData } from 'src/components/ServiceRequestForm/CustomHooks/useServiceRequestFormFlow';

export const useServiceRequestSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const rollbackUploadedS3Files = async (uploadedFiles: string[]) => {
    if (uploadedFiles.length === 0) return;

    console.error('Rolling back uploaded files:', uploadedFiles);

    const deletePromises = uploadedFiles.map(async (fileUrl: string) => {
      const deleteResult: DeleteFileS3Result =
        await deleteServiceRequestFileFromS3(fileUrl);

      if (deleteResult.success === false) {
        console.error('Failed to delete:', fileUrl, deleteResult.error);
      }
    });

    await Promise.all(deletePromises);

    console.error(
      "' 🚫 Service request creation failed. Uploaded files have been rolled back.'"
    );
  };

  /**
   * Core service request creation logic with rollback support.
   * Uploads files to S3, creates the service request, and rolls back files if SR creation fails.
   * @returns The created service request data
   * @throws Error if upload or SR creation fails (triggers a doc rollback)
   */
  const createServiceRequestWithAttachedFiles = async (
    formData: ServiceRequestFormData,
    annotations: Record<string, string>,
    note?: ServiceRequestCreationNote
  ) => {
    const uploadedFilesForRollback: string[] = [];

    try {
      const srAttachments: any = formData.srAttachments || [];

      let attachmentsToUpload: any[];

      try {
        attachmentsToUpload = await applyAnnotationsToAttachments(
          srAttachments,
          annotations
        );
      } catch (annotationErr: any) {
        console.warn(
          'Annotation conversion failed before upload; aborting submit so no partial request is sent.',
          annotationErr
        );

        throw annotationErr;
      }

      const { filesToUpload, existingUrls } =
        separateAttachments(attachmentsToUpload);

      const uploadedUrls: string[] = [...existingUrls];

      // Upload new files to S3 - TODO this is pretty bad happening in a loop like this. Later on we should refactor this to batch attachments.
      for (const file of filesToUpload) {
        const uploadResult: UploadResult =
          await uploadServiceRequestFileToS3(file);

        if (uploadResult.success === false) {
          toast.error(`Upload failed: ${uploadResult.error}`);
          throw new Error(uploadResult.error);
        }

        uploadedUrls.push(uploadResult.url);
        uploadedFilesForRollback.push(uploadResult.url);
      }

      // Create the service request on the API.
      // legacy: prepareServiceRequestCreationPayload still types against the
      // pre-refactor ServiceRequestFormData (extra/typo'd fields). The flow-hook
      // ServiceRequestFormData is the canonical runtime shape; cast at this seam.
      const serviceRequestPayload: ServiceRequestCreationPayload =
        prepareServiceRequestCreationPayload(formData as any, uploadedUrls);

      const payloadWithNote: ServiceRequestCreationPayload = note
        ? { ...serviceRequestPayload, note }
        : serviceRequestPayload;

      const response: any = await createServiceRequest(payloadWithNote);

      if (!response.ok || response.status !== 200) {
        throw new Error('Failed to create service request');
      }

      const responseJson = await response.json();

      toast.success('ⓘ Service request created successfully.', {
        autoClose: 12000,
        position: 'top-center',
      });

      return {
        srData: responseJson.data,
        serviceRequestPayload,
      };
    } catch (err: any) {
      await rollbackUploadedS3Files(uploadedFilesForRollback);
      console.error('Error during service request creation:', err);
      toast.error(
        ' 🚫 Something went wrong with adding a service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );

      throw err;
    }
  };


  /* Count invalidation after a successful create. Does NOT trigger rollback if it
   * fails, since the SR already exists.
   * TODO(sr-form-redux-exit): these dispatches refresh OTHER pages' redux count
   * caches. Navigation/reseed no longer lives here — the Details orchestrator drives
   * that via the flow hook. Remove once those pages read counts via hooks. */
  const invalidateCounts = (serviceRequestPayload: any, from: any) => {
    try {
      if (from === 'Suite') {
        dispatch(fetchSuiteCounts(serviceRequestPayload.suiteId));
      }

      if (from === 'Property') {
        const user = selectAuthenticatedUser(store.getState());
        dispatch(
          fetchPropertyCounts(
            serviceRequestPayload.propertyId,
            user.id,
            user.roles[0].role.name
          )
        );
      }
    } catch (err: any) {
      console.error('Error invalidating counts:', err);
    }
  };

  const submitServiceRequest = async (
    formData: ServiceRequestFormData,
    from: any,
    annotations: Record<string, string>,
    note?: ServiceRequestCreationNote
  ) => {
    setIsSubmitting(true);

    try {
      const { srData, serviceRequestPayload } =
        await createServiceRequestWithAttachedFiles(formData, annotations, note);

      invalidateCounts(serviceRequestPayload, from);

      return {
        success: true,
        data: srData,
      };
    } catch (err: any) {
      console.error('Service request submission failed:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitServiceRequest,
    isSubmitting,
  };
};
